import React, { useEffect, useState } from 'react';
import { subscriptionManager } from '../../lib/subscription-manager';

/**
 * Component for monitoring real-time database subscription statistics and displaying them in development mode
 * This is helpful for identifying subscription churn issues
 * Only visible in development mode (press Alt+S to toggle visibility)
 */
export function SubscriptionMonitor() {
  const [stats, setStats] = useState(() => subscriptionManager.getStats());
  const [visible, setVisible] = useState(false);
  
  // Only show this component in development mode
  const isDev = process.env.NODE_ENV === 'development';
  
  useEffect(() => {
    if (!isDev) return;
    
    // Update stats every 2 seconds
    const interval = setInterval(() => {
      setStats(subscriptionManager.getStats());
    }, 2000);
    
    // Toggle visibility with keyboard shortcut (Alt+S)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 's') {
        setVisible(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDev]);
  
  // Don't render anything in production or if not visible
  if (!isDev || !visible) return null;
  
  // Calculate color based on subscription count
  const getColor = (count: number) => {
    if (count <= 10) return 'text-green-500';
    if (count <= 20) return 'text-yellow-500';
    return 'text-red-500';
  };
  
  // Get table stats if available
  const tableStats = stats.subscriptionsByTable ? 
    Object.entries(stats.subscriptionsByTable).map(([table, count]) => (
      <div key={table} className="flex justify-between my-1">
        <span className="font-mono">{table}:</span>
        <span className={getColor(count)}>{count}</span>
      </div>
    )) : null;
  
  return (
    <div className="fixed bottom-4 right-4 bg-black/80 text-white p-4 rounded-lg shadow-lg z-50 text-sm max-w-xs">
      <h3 className="font-bold text-center border-b border-gray-500 pb-1 mb-2">
        Subscription Monitor
      </h3>
      
      <div className="flex justify-between font-semibold">
        <span>Total Subscriptions:</span>
        <span className={getColor(stats.totalSubscriptions)}>{stats.totalSubscriptions}</span>
      </div>
      
      <div className="flex justify-between mt-1">
        <span>Active Subscriptions:</span>
        <span className={getColor(stats.activeSubscriptions)}>{stats.activeSubscriptions}</span>
      </div>
      
      <div className="flex justify-between mt-1">
        <span>Total Subscribers:</span>
        <span className={getColor(stats.subscriberCount || 0)}>{stats.subscriberCount || 0}</span>
      </div>
      
      {stats.pendingRemovals !== undefined && (
        <div className="flex justify-between mt-1">
          <span>Pending Removals:</span>
          <span>{stats.pendingRemovals}</span>
        </div>
      )}
      
      {stats.pendingCreations !== undefined && (
        <div className="flex justify-between mt-1">
          <span>Pending Creations:</span>
          <span>{stats.pendingCreations}</span>
        </div>
      )}
      
      {tableStats && tableStats.length > 0 && (
        <div className="mt-2 border-t border-gray-500 pt-1">
          <div className="font-semibold mb-1">Subscriptions by Table:</div>
          {tableStats}
        </div>
      )}
      
      <div className="mt-2 text-xs text-gray-400 text-center">
        Press Alt+S to toggle
      </div>
    </div>
  );
}